import Loading from "@/components/loading/loading.vue";
import AnnotationState from "@/modules/annotation";
import { Modal } from "ant-design-vue";
import { fabric } from "fabric";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Annotation } from "../../models/image-meta-data";
import { ImageEditorFlipAxis, ImageEditorFlipEvent, ImageEditorRotateEvent } from "../image-editor";
import { AnnotationTypes, ShapeGetData } from "./objects/annotation-type";
import { Circle } from "./objects/circle.fabric";
import { Rectangle } from "./objects/rectangle.fabric";
import { Star } from "./objects/star.fabric";

const defaultPosition = {
	x: 20,
	y: 20
};
@Component({
	components: {
		Modal,
		Loading
	}
})
export default class AnnotationView extends Vue {
	@Prop({ default: () => [] }) annotations!: Annotation[];
	@Prop({ default: false }) isEditMode!: boolean;

	private canvas: fabric.Canvas = (null as any) as fabric.Canvas;
	private deleteIconImage: HTMLImageElement | null = null;
	private ratio: number = 1;
	private annotationStore = getModule(AnnotationState);

	public $refs!: {
		annotationContainerRef: HTMLDivElement;
	};

	copiedShape: fabric.Object | null = null;

	mounted() {
		this.setupCanvas();
	}

	@Watch("isEditMode")
	onEditModeChanged(currentValue: boolean) {
		if (currentValue) {
			window.addEventListener("keydown", this.handleKeyDown);
		} else {
			window.removeEventListener("keydown", this.handleKeyDown);
		}
	}
	@Watch("annotations")
	update() {
		this.reset();
	}

	private registerCanvasEvent() {
		this.canvas.on("mouse:down", this.handleMouseClick);
		this.canvas.on("object:added", this.handleObjectAdded);
	}
	private handleObjectAdded(event) {
		if (event.target.isCloneObj) {
			// if the target is cloned object, assign getData method for it
			event.target.getData = function () {
				const { width, height, scaleX, scaleY, stroke, fill, angle, opacity, type } = event.target;
				const w = width!;
				const h = height!;
				var matrix = event.target.calcTransformMatrix();
				const x = matrix[4]; // x to canvas
				const y = matrix[5]; // y to canvas
				return {
					type: type === "rect" ? AnnotationTypes.Rect : AnnotationTypes.Circle,
					width: w * scaleX,
					height: h * scaleY,
					x,
					y,
					color: stroke!,
					rotate: angle || 0,
					fill: fill,
					opacity: opacity ? opacity : 1
				};
			};
		}
	}

	private handleMouseClick(event) {
		if (this.annotationStore.isFillingColor) {
			if (event.target && event.target.type !== "activeSelection") {
				event.target.set("fill", this.annotationStore.currentColor);
				event.target.set("stroke", this.annotationStore.currentColor);
				event.target.set("opacity", 0.4);
				this.canvas.renderAll();
			}
			this.annotationStore.toggleFillColor();
		}
	}

	public drawExistingAnnotations() {
		this.annotations.forEach((annotation) => {
			this.addAnnotation(annotation);
		});
	}

	public getAnnotations(): Annotation[] {
		const objects = this.canvas.getObjects();
		const annotations = objects
			.filter((object) => this.isObjectAnnotation(object))
			.map((object) => ((object as any) as ShapeGetData).getData());
		return annotations;
	}

	@Watch("annotationStore.isFillingColor")
	handleIsFillingColor(currentValue: boolean) {
		const upperCanvasEl = document.getElementsByClassName("upper-canvas")[0] as HTMLElement;
		if (!upperCanvasEl) return;
		if (currentValue) {
			upperCanvasEl.classList.add("fill-color-cursor");
		} else {
			upperCanvasEl.classList.remove("fill-color-cursor");
		}
		this.canvas.renderAll();
	}

	private addAnnotation(annotation: Partial<Annotation>) {
		const shape = {
			width: 100,
			height: 100,
			x: 10,
			y: 10,
			rotate: 0,
			opacity: 1,
			...annotation
		} as Annotation;
		switch (shape.type) {
			case AnnotationTypes.Rect:
				this.canvas.add(new Rectangle(shape));
				break;
			case AnnotationTypes.Circle:
				this.canvas.add(new Circle(shape));
				break;
			case AnnotationTypes.Star:
				this.canvas.add(new Star(shape));
				break;
		}
	}

	public dropAnnotation(annotation: Partial<Annotation>) {
		const { left, top, width, height } = this.$refs.annotationContainerRef.children[0].getBoundingClientRect();

		// Check if the annotation is dropped in the canvas
		if (!(annotation.x! >= left && annotation.x! <= left + width)) {
			return;
		}
		if (!(annotation.y! >= top && annotation.y! <= top + height)) {
			return;
		}

		// Subtraction of screen offset
		const halfOfDefaultSize = 50 * this.ratio;
		const xInsideContainer = annotation.x! - left - halfOfDefaultSize;
		const yInsideContainer = annotation.y! - top - halfOfDefaultSize;

		// Subtraction of ratio to match the coords if the zoom is applied
		const x = xInsideContainer / this.ratio;
		const y = yInsideContainer / this.ratio;

		this.addAnnotation({ ...annotation, x, y });
	}

	private isObjectAnnotation(object: fabric.Object | any): boolean {
		if (object instanceof Rectangle || object instanceof Circle || object instanceof Star || object.isCloneObj) {
			return true;
		}
		return false;
	}

	private iterateObjects(iterateFunction: (object: Rectangle | Circle | Star | any) => void) {
		this.canvas.getObjects().forEach((object) => {
			if (this.isObjectAnnotation(object)) {
				iterateFunction(object as Rectangle | Circle | Star | any);
			}
		});
	}

	public updateSize(imageEditorcanvasData: Cropper.CanvasData) {
		const { naturalWidth, width, height, left, top } = imageEditorcanvasData;
		this.ratio = width / naturalWidth;
		this.canvas.setDimensions({ width, height });
		this.canvas.setZoom(this.ratio);
		const canvasContainer = document.querySelector<HTMLDivElement>(".annotation-canvas");
		if (canvasContainer) {
			canvasContainer.style.transform = `translate(${left}px, ${top}px)`;
		}
	}

	public rotate(event: ImageEditorRotateEvent) {
		this.iterateObjects((object) => {
			const { width, height, x, y, ...rest } = object.getData();

			// Removing the old object
			this.canvas.remove(object);

			let newX = y;
			let newY = x;

			if (event.clockWise) {
				newX = event.canvasData.naturalWidth - (y + height);
			} else {
				newY = event.canvasData.naturalHeight - (x + width);
			}

			this.addAnnotation({
				x: newX,
				y: newY,
				width: height,
				height: width,
				...rest
			});
		});
		this.canvas.renderAll();
	}
	public flip(event: ImageEditorFlipEvent) {
		this.iterateObjects((object) => {
			const { width, height, x, y, ...rest } = object.getData();
			let newX = x;
			let newY = y;
			if (event.axis === ImageEditorFlipAxis.horizontal) {
				newX = event.canvasData.naturalWidth - x - width;
			}
			if (event.axis === ImageEditorFlipAxis.vertical) {
				newY = event.canvasData.naturalHeight - y - height;
			}
			if (!object.isCloneObj) {
				object.setData({
					x: newX,
					y: newY
				});
			} else {
				this.canvas.remove(object);
				this.addAnnotation({
					x: newX,
					y: newY,
					width: height,
					height: width,
					...rest
				});
			}
		});
		this.canvas.renderAll();
	}
	public restore() {
		this.deleteAllAnnotations();
	}

	private setupCanvas() {
		// If canvas instance is already exists, we need to dispose that instance to start over
		if (this.canvas) {
			this.canvas.dispose();
			this.canvas = null as any;
		}

		// Creating an instance of FabricJS Canvas
		this.canvas = new fabric.Canvas("canvas", { containerClass: "annotation-canvas" });

		// Febric JS object selection color - making it a little appealing
		fabric.Object.prototype.set({
			transparentCorners: false,
			borderColor: "#63CBAB",
			cornerColor: "#87E5CA"
		});
		this.registerCanvasEvent();

		// Set the delete icon for any annotation
		this.setDeleteIcon();

		// Drawing existing annotations
		this.drawExistingAnnotations();
	}

	private setDeleteIcon() {
		const img = document.createElement("img");
		img.src =
			"data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg version='1.1' id='Ebene_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='595.275px' height='595.275px' viewBox='200 215 230 470' xml:space='preserve'%3E%3Ccircle style='fill:%23F44336;' cx='299.76' cy='439.067' r='218.516'/%3E%3Cg%3E%3Crect x='267.162' y='307.978' transform='matrix(0.7071 -0.7071 0.7071 0.7071 -222.6202 340.6915)' style='fill:white;' width='65.545' height='262.18'/%3E%3Crect x='266.988' y='308.153' transform='matrix(0.7071 0.7071 -0.7071 0.7071 398.3889 -83.3116)' style='fill:white;' width='65.544' height='262.179'/%3E%3C/g%3E%3C/svg%3E";
		this.deleteIconImage = img;

		fabric.Object.prototype.controls.deleteControl = new fabric.Control({
			x: 0.5,
			y: -0.5,
			offsetY: 16,
			cursorStyle: "pointer",
			mouseUpHandler: this.deleteObject.bind(this),
			render: this.renderIcon,
			cornerSize: 24
		} as any);
	}

	private deleteObject(): boolean {
		const activeObjects = this.canvas.getActiveObjects();
		this.canvas.remove(...activeObjects);
		this.canvas.discardActiveObject();
		return false;
	}

	private renderIcon(ctx: any, left: any, top: any, styleOverride: any, fabricObject: any) {
		var size = 24;
		ctx.save();
		ctx.translate(left, top);
		ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
		ctx.drawImage(this.deleteIconImage, -size / 2, -size / 2, size, size);
		ctx.restore();
	}

	private reset() {
		// Removing existing annotations
		this.deleteAllAnnotations();

		// Adding new annotations which we get from prop
		this.drawExistingAnnotations();
	}

	private deleteAllAnnotations() {
		const objects = this.canvas.getObjects().filter(this.isObjectAnnotation);
		this.canvas.remove(...objects);
	}

	private renderClonedToCanvasCb(cloned: any) {
		if (!cloned) return;
		this.canvas.discardActiveObject();
		const that = this;
		const commonProps = {
			evented: true,
			top: defaultPosition.y + (cloned.height * cloned.scaleY) / 2,
			left: defaultPosition.x + (cloned.width * cloned.scaleX) / 2
		};
		if (cloned.type === "activeSelection") {
			cloned.set({
				...commonProps,
				originX: "center",
				originY: "center"
			});
			cloned.canvas = this.canvas;
			cloned.forEachObject(function (obj) {
				obj.isCloneObj = true;
				obj.set({ strokeUniform: true });
				that.canvas.add(obj);
			});
			cloned.setCoords();
		} else {
			cloned.set({
				...commonProps,
				strokeUniform: true
			});
			cloned.isCloneObj = true;
			this.canvas.add(cloned);
		}
		this.canvas.setActiveObject(cloned);
		this.canvas.renderAll();
	}

	private archiveClonedCb(cloned) {
		this.copiedShape = cloned;
	}

	// copy by clone icon in toolbox
	public cloneActiveShape() {
		const activeShape = this.canvas.getActiveObject();
		if (!activeShape) return;
		activeShape.clone(this.renderClonedToCanvasCb);
	}

	public removeActiveShape() {
		const activeShapes = this.canvas.getActiveObjects();
		if (!activeShapes) return;
		this.canvas.remove(...activeShapes);
		this.canvas.discardActiveObject();
	}

	// copy and paste by keyboard
	private handleCopyByKeyboard() {
		const activeShape = this.canvas.getActiveObject();
		if (!activeShape) return;
		activeShape.clone(this.archiveClonedCb);
	}

	private handlePasteByKeyboard() {
		if (!this.copiedShape) return;
		this.copiedShape.clone(this.renderClonedToCanvasCb);
	}

	private handleKeyDown(event: KeyboardEvent) {
		event.preventDefault();
		const charCode = String.fromCharCode(event.which).toLowerCase();
		if ((event.ctrlKey || event.metaKey) && charCode === "c") {
			this.handleCopyByKeyboard();
		} else if ((event.ctrlKey || event.metaKey) && charCode === "v") {
			this.handlePasteByKeyboard();
		} else if (event.key === "Delete" || event.key === "Backspace") {
			this.removeActiveShape();
		}
	}
}
